import { type DragEvent, useMemo, useState } from 'react';
import { MatchConfidenceBadge } from '~/features/animation/MatchConfidenceBadge';
import { useActiveClip } from '~/features/clips/selectors';
import { useCleanedBitmap } from '~/features/source/SharedBitmapContext';
import { useStore } from '~/store';
import type { Frame, MatchResult } from '~/types/pipeline';
import { FrameThumb } from './FrameThumb';
import { useFrames } from './useFrames';

const THUMB_SIZE = 48;

type FreePositions = Record<number, { row: number; col: number }>;

function freeOrderedFrames(frames: readonly Frame[], positions: FreePositions): Frame[] {
  return [...frames].sort((a, b) => {
    const pa = positions[a.index];
    const pb = positions[b.index];
    if (!pa && !pb) return a.index - b.index;
    if (!pa) return 1;
    if (!pb) return -1;
    if (pa.row !== pb.row) return pa.row - pb.row;
    if (pa.col !== pb.col) return pa.col - pb.col;
    return a.index - b.index;
  });
}

function positionsFromOrder(orderedIndices: readonly number[]): FreePositions {
  const out: FreePositions = {};
  orderedIndices.forEach((index, i) => {
    out[index] = { row: 0, col: i };
  });
  return out;
}

function reorderInsert(
  base: readonly number[],
  draggedIndex: number,
  targetIndex: number,
): number[] | null {
  if (draggedIndex === targetIndex) return null;
  const without = base.filter((i) => i !== draggedIndex);
  const at = without.indexOf(targetIndex);
  if (at < 0) return null;
  without.splice(at, 0, draggedIndex);
  return without;
}

function arraysEqual(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export function FramesGrid() {
  const bitmap = useCleanedBitmap();
  const frames = useFrames();
  const layoutMode = useStore((s) => s.atlas.layoutMode);
  const setLayoutMode = useStore((s) => s.setLayoutMode);
  const activeClip = useActiveClip();
  const matches = useStore((s) => s.referencePoint.results);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [previewOrder, setPreviewOrder] = useState<number[] | null>(null);

  const isFreeMode = layoutMode.kind === 'free';
  const isReferencePoint = activeClip?.alignmentMode === 'reference-point';

  const matchByFrameIndex = useMemo(() => {
    if (!isReferencePoint || !matches) return new Map<number, MatchResult>();
    return new Map(matches.map((match) => [match.frameIndex, match]));
  }, [isReferencePoint, matches]);

  const baseOrder = useMemo(() => {
    if (!isFreeMode || layoutMode.kind !== 'free') return frames.map((f) => f.index);
    return freeOrderedFrames(frames, layoutMode.positions).map((f) => f.index);
  }, [frames, isFreeMode, layoutMode]);

  const displayOrder = previewOrder ?? baseOrder;

  const displayFrames = useMemo(() => {
    const byIndex = new Map(frames.map((f) => [f.index, f]));
    return displayOrder.map((i) => byIndex.get(i)).filter((f): f is Frame => f !== undefined);
  }, [frames, displayOrder]);

  if (!bitmap || frames.length === 0) {
    return <p className="text-xs text-muted-foreground">No frames yet</p>;
  }
  const sourceBitmap = bitmap;

  const moveToEnd = (base: readonly number[], draggedFrameIndex: number): number[] => {
    const without = base.filter((i) => i !== draggedFrameIndex);
    without.push(draggedFrameIndex);
    return without;
  };

  const onTileDragOver = (frameIndex: number) => (event: DragEvent<HTMLButtonElement>) => {
    if (!isFreeMode || dragIndex === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    const next = reorderInsert(displayOrder, dragIndex, frameIndex);
    if (next && !arraysEqual(next, displayOrder)) setPreviewOrder(next);
  };

  const onTileDrop = (frameIndex: number) => (event: DragEvent<HTMLButtonElement>) => {
    if (!isFreeMode || dragIndex === null) return;
    event.preventDefault();
    const next = reorderInsert(displayOrder, dragIndex, frameIndex) ?? previewOrder ?? baseOrder;
    setLayoutMode({ kind: 'free', positions: positionsFromOrder(next) });
    setDragIndex(null);
    setPreviewOrder(null);
  };

  const onEndDragOver = (event: DragEvent<HTMLButtonElement>) => {
    if (!isFreeMode || dragIndex === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    const next = moveToEnd(displayOrder, dragIndex);
    if (!arraysEqual(next, displayOrder)) setPreviewOrder(next);
  };

  const onEndDrop = (event: DragEvent<HTMLButtonElement>) => {
    if (!isFreeMode || dragIndex === null) return;
    event.preventDefault();
    const next = moveToEnd(displayOrder, dragIndex);
    setLayoutMode({ kind: 'free', positions: positionsFromOrder(next) });
    setDragIndex(null);
    setPreviewOrder(null);
  };

  return (
    <fieldset
      aria-label="Frames layout preview"
      className="m-0 grid min-w-0 grid-cols-4 gap-1 border-0 p-0"
    >
      {displayFrames.map((frame) => {
        const match = isReferencePoint ? matchByFrameIndex.get(frame.index) : null;
        const isDragging = dragIndex === frame.index;
        return (
          <button
            key={frame.index}
            type="button"
            draggable={isFreeMode}
            aria-label={`Frame ${frame.index}`}
            className={`relative border-0 bg-transparent p-0 text-left${
              isFreeMode ? ' cursor-move' : ''
            }${isDragging ? ' opacity-30' : ''}`}
            onDragStart={(event) => {
              if (!isFreeMode) return;
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', String(frame.index));
              setDragIndex(frame.index);
              setPreviewOrder(baseOrder);
            }}
            onDragOver={onTileDragOver(frame.index)}
            onDrop={onTileDrop(frame.index)}
            onDragEnd={() => {
              setDragIndex(null);
              setPreviewOrder(null);
            }}
          >
            <FrameThumb frame={frame} source={sourceBitmap} size={THUMB_SIZE} />
            {match && <MatchConfidenceBadge match={match} />}
          </button>
        );
      })}
      {isFreeMode && (
        <button
          type="button"
          aria-label="Drop at end"
          className={`rounded border border-dashed transition-colors ${
            dragIndex !== null ? 'border-primary/60 bg-primary/5' : 'border-transparent'
          }`}
          style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
          onDragEnter={(e) => {
            if (dragIndex !== null) e.preventDefault();
          }}
          onDragOver={onEndDragOver}
          onDrop={onEndDrop}
        />
      )}
    </fieldset>
  );
}
